import { useCallback, useEffect, useMemo, useState } from "react";
import { assistAdsLibraryDraft, type AdsLibraryAssistDraft } from "@/lib/adsAssistantClient";
import type { AdsAssistantSocialPostRow } from "@/lib/adsAssistantClientTypes";
import { listAdsSocialPosts } from "@/lib/adsAssistantClientSocial";
import { platformOptionsFromOrgChecklistConfig, type AdsPlatformSelectOption } from "@/lib/orgAdsPlatformOptions";
import { fetchOrgLocations } from "@/lib/locations/fetchOrgLocations";
import { requireSupabase, supabase } from "@/lib/supabase";

export const EXAMPLE_BANK_PAGE_SIZE = 10;
export const EXAMPLE_BANK_MAX_SELECT = 5;

type LocationOpt = { id: string; name: string };

export function useAdsLibraryExampleBankAssist(scopedOrgId: string | null) {
  const [platformOptions, setPlatformOptions] = useState<AdsPlatformSelectOption[]>(() =>
    platformOptionsFromOrgChecklistConfig(null)
  );
  const [locations, setLocations] = useState<LocationOpt[]>([]);
  const [platform, setPlatform] = useState("");
  const [locationId, setLocationId] = useState("");
  const [searchDraft, setSearchDraft] = useState("");
  const [searchApplied, setSearchApplied] = useState("");
  const [page, setPage] = useState(0);
  const [posts, setPosts] = useState<AdsAssistantSocialPostRow[]>([]);
  const [total, setTotal] = useState(0);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [tplDraft, setTplDraft] = useState<AdsLibraryAssistDraft | null>(null);
  const [catDraft, setCatDraft] = useState<AdsLibraryAssistDraft | null>(null);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [suggestError, setSuggestError] = useState<string | null>(null);

  useEffect(() => {
    if (!scopedOrgId || !supabase) {
      setPlatformOptions(platformOptionsFromOrgChecklistConfig(null));
      setLocations([]);
      return;
    }
    const sb = requireSupabase();
    void (async () => {
      const { data: orgRow } = await sb
        .from("organizations")
        .select("sales_daily_checklist_config")
        .eq("id", scopedOrgId)
        .maybeSingle();
      const raw = (orgRow as { sales_daily_checklist_config?: unknown } | null)?.sales_daily_checklist_config;
      setPlatformOptions(platformOptionsFromOrgChecklistConfig(raw));

      const { data: locRows } = await fetchOrgLocations(sb, scopedOrgId);
      setLocations((locRows as LocationOpt[]) ?? []);
    })();
  }, [scopedOrgId]);

  useEffect(() => {
    if (!platformOptions.length) return;
    setPlatform((p) => (p && platformOptions.some((o) => o.key === p) ? p : platformOptions[0]!.key));
  }, [platformOptions]);

  const loadPosts = useCallback(async () => {
    if (!scopedOrgId || !platform) return;
    setListLoading(true);
    setListError(null);
    const r = await listAdsSocialPosts({
      platform,
      location_id: locationId || undefined,
      search: searchApplied.trim() || undefined,
      limit: EXAMPLE_BANK_PAGE_SIZE,
      offset: page * EXAMPLE_BANK_PAGE_SIZE,
    });
    setListLoading(false);
    if (!r.ok) {
      setListError(r.error);
      setPosts([]);
      setTotal(0);
      return;
    }
    setPosts(r.data.posts ?? []);
    setTotal(typeof r.data.total === "number" ? r.data.total : 0);
  }, [scopedOrgId, platform, locationId, searchApplied, page]);

  useEffect(() => {
    void loadPosts();
  }, [loadPosts]);

  const pageCount = Math.max(1, Math.ceil(total / EXAMPLE_BANK_PAGE_SIZE));

  const toggleRow = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else {
        if (next.size >= EXAMPLE_BANK_MAX_SELECT) return prev;
        next.add(id);
      }
      return next;
    });
  }, []);

  const selectedIds = useMemo(() => [...selected], [selected]);

  const resetPagingAndSelection = useCallback(() => {
    setPage(0);
    setSelected(new Set());
  }, []);

  const runSuggestions = useCallback(async () => {
    setSuggestError(null);
    setTplDraft(null);
    setCatDraft(null);
    if (!platform || selectedIds.length < 1) {
      setSuggestError(`Pick an ads platform and between 1 and ${EXAMPLE_BANK_MAX_SELECT} posts.`);
      return;
    }
    setSuggestLoading(true);
    try {
      const common = {
        intent:
          "Infer a reusable library template (format guidance) and a taxonomy category label from the indexed examples only.",
        pastedAd: "",
        includeSampleAdCopy: false as const,
        exampleSocialPostIds: selectedIds,
        examplePlatform: platform,
      };
      const [rt, rc] = await Promise.all([
        assistAdsLibraryDraft({ ...common, target: "template" }),
        assistAdsLibraryDraft({ ...common, target: "category" }),
      ]);
      setSuggestLoading(false);

      const errs: string[] = [];
      if (!rt.ok) errs.push(`Template: ${rt.error}`);
      else setTplDraft(rt.data.draft.template ? rt.data.draft : null);

      if (!rc.ok) errs.push(`Category: ${rc.error}`);
      else setCatDraft(rc.data.draft.category ? rc.data.draft : null);

      if (errs.length) setSuggestError(errs.join(" "));
    } catch {
      setSuggestLoading(false);
      setSuggestError("Suggestions failed. Try again.");
    }
  }, [platform, selectedIds]);

  return {
    platformOptions,
    locations,
    platform,
    setPlatform,
    locationId,
    setLocationId,
    searchDraft,
    setSearchDraft,
    searchApplied,
    setSearchApplied,
    page,
    setPage,
    posts,
    total,
    listLoading,
    listError,
    selected,
    toggleRow,
    selectedIds,
    tplDraft,
    catDraft,
    suggestLoading,
    suggestError,
    pageCount,
    resetPagingAndSelection,
    runSuggestions,
  };
}
